import React, { useEffect, useRef } from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import { PricingPlans } from '../Pricing/PricingPlans';
import { CheckoutAuthPrompt } from '../Pricing/CheckoutAuthPrompt';
import { useCheckout } from '../../hooks/useCheckout';
import type { CheckoutPlanId } from '../../data/pricingPlans';
import { trackCheckoutStarted, trackPricingViewed } from '../../utils/analytics';
import { trackEvent, trackCtaClick } from '../../utils/visitorTracking';
export function PricingSection() {
  const {
    onCheckout,
    pendingPlan,
    authPromptPlan,
    dismissAuthPrompt
  } = useCheckout('homepage_pricing_section');
  // This section is mounted lazily as it scrolls into view, so mount == viewed.
  // Ref guard so Strict Mode's double-invoked effect logs one view
  const viewTracked = useRef(false);
  useEffect(() => {
    if (viewTracked.current) return;
    viewTracked.current = true;
    trackPricingViewed('homepage_pricing_section');
    void trackEvent('pricing_viewed', {
      location: 'homepage_pricing_section'
    });
    void trackEvent('premium_view', {
      location: 'homepage_pricing_section'
    });
  }, []);
  const handleCheckout = (plan: CheckoutPlanId) => {
    trackCheckoutStarted(plan, 'homepage_pricing_section');
    trackCtaClick(plan, 'homepage_pricing_section', 'checkout');
    void trackEvent('premium_checkout_started', {
      plan,
      location: 'homepage_pricing_section'
    });
    void onCheckout(plan);
  };
  return <section className="relative bg-[#050505] py-24" id="pricing" data-id="element-2588" data-mp="3oclc5l7e5uhi">
      <div className="absolute left-0 top-0 flex h-8 w-full items-center overflow-hidden bg-[#FFD700] shadow-lg" data-id="element-2589" data-mp="3kexfacm51rmz">
        <div className="flex whitespace-nowrap animate-marquee" data-id="element-2590" data-mp="20oa01g95izl">
          {Array.from({
          length: 20
        }, (_, index) => <span key={index} className="mx-4 flex items-center gap-2 text-sm font-black uppercase tracking-widest text-black" data-id="element-2591" data-mp="35n59osc5bryg">
                <AlertTriangle size={16} fill="black" data-id="element-2592" data-mp="14n0dd53xaeyw" /> CAUTION: TOXICITY
                DETECTED
              </span>)}
        </div>
      </div>
      <style data-id="element-2593" data-mp="1ec1tcvagwwcv">{`
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee { animation: marquee 20s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .animate-marquee { animation: none; } }
      `}</style>

      <div className="mx-auto max-w-6xl px-6 pt-16" data-id="element-2594" data-mp="lppdpjedpjix">
        <header className="mx-auto mb-14 max-w-2xl text-center" data-id="element-2595" data-mp="qn8tinqq64cz">
          <h2 className="text-4xl font-black uppercase leading-none tracking-tight text-white md:text-5xl" data-id="element-2596" data-mp="vl0x4f0qfex0">
            Invest In Your <span className="text-[#FFD700]" data-id="element-2597" data-mp="zo6yb88zmfwm">Sanity</span>
          </h2>
          <p className="mt-5 font-mono text-lg leading-relaxed text-gray-400" data-id="element-2598" data-mp="270z8wqnaof1x">
            Choose the level of clarity that fits this conversation.
          </p>
        </header>

        <PricingPlans onCheckout={handleCheckout} pendingPlan={pendingPlan} data-id="element-2599" data-mp="2b45c1pf41q46" />

        <p className="mt-8 flex items-center justify-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-gray-500" data-id="element-2600" data-mp="3vjk2c83m7hoa">
          <Lock size={10} aria-hidden="true" data-id="element-2601" data-mp="3mrrt20qbuela" /> Secure checkout · Cancel
          anytime
        </p>
      </div>

      <CheckoutAuthPrompt plan={authPromptPlan} onClose={dismissAuthPrompt} data-id="element-2602" data-mp="3htgkrufokc56" />
    </section>;
}